/**
 * Agent Portal Service
 * Handles all API calls for the AI Search Portal agent functionality
 */

import axiosInstance from "../helpers/api";

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  subdomain: string;
  phone?: string;
  planId: string;
}

interface AgentProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  subdomain: string;
  stripeCustomerId?: string;
  subscriptionStatus: string;
  planName: string;
  portalUrl?: string;
  [key: string]: any;
}

class AgentPortalService {
  private basePath = "/agents";

  /**
   * Agent authentication endpoints
   */
  async login(credentials: LoginCredentials) {
    try {
      // Use the agents login endpoint which is working
      const response = await axiosInstance.post(`${this.basePath}/login`, credentials);
      
      console.log('Login response:', response.data);
      
      // Store auth token if provided
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('agent_token', response.data.token);
      }
      
      // Store user info and plan
      if (response.data.user) {
        localStorage.setItem('user_info', JSON.stringify(response.data.user));
        if (response.data.user.planId) {
          localStorage.setItem('agent_plan', response.data.user.planId);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  }

  async signup(data: SignupData) {
    const response = await axiosInstance.post(`${this.basePath}/signup`, data);
    
    // Store auth token if provided
    if (response.data.token) {
      localStorage.setItem('agent_token', response.data.token);
    }
    
    return response.data;
  }

  async logout() {
    localStorage.removeItem('agent_token');
    await axiosInstance.post(`${this.basePath}/logout`);
  }

  async getProfile(): Promise<AgentProfile> {
    const response = await axiosInstance.get(`${this.basePath}/profile`);
    return response.data;
  }

  async updateProfile(data: Partial<AgentProfile>) {
    const response = await axiosInstance.put(`${this.basePath}/profile`, data);
    return response.data;
  }

  /**
   * Portal Settings Management
   */
  async getPortalSettings() {
    const response = await axiosInstance.get('/portal-settings/my-settings');
    return response.data;
  }

  async updatePortalSettings(data: {
    slug?: string;
    display_name?: string;
    bio?: string;
    phone?: string;
    email?: string;
    brokerage_name?: string;
    license_number?: string;
    years_experience?: number;
    specialties?: string[];
    service_areas?: string[];
    social_links?: any;
    is_published?: boolean;
    brand_color?: string;
    logo_url?: string;
    welcome_message?: string;
  }) {
    const response = await axiosInstance.put('/portal-settings/my-settings', data);
    return response.data;
  }

  async checkSlugAvailability(slug: string) {
    const response = await axiosInstance.get(`/portal-settings/check-slug/${slug}`);
    return response.data;
  }

  /**
   * Subscription management
   */
  async createCheckoutSession(planId: string) {
    const response = await axiosInstance.post(`${this.basePath}/create-checkout-session`, {
      planId
    });
    return response.data;
  }

  async getSubscriptionStatus() {
    const response = await axiosInstance.get(`${this.basePath}/subscription-status`);
    return response.data;
  }

  async cancelSubscription() {
    const response = await axiosInstance.post(`${this.basePath}/cancel-subscription`);
    return response.data;
  }

  /**
   * Client management
   */
  async getClients(params?: { page?: number; limit?: number; search?: string }) {
    const response = await axiosInstance.get(`${this.basePath}/clients`, { params });
    return response.data;
  }

  async getClient(clientId: string) {
    const response = await axiosInstance.get(`${this.basePath}/clients/${clientId}`);
    return response.data;
  }

  async inviteClient(data: { email: string; firstName: string; lastName: string }) {
    const response = await axiosInstance.post(`${this.basePath}/clients/invite`, data);
    return response.data;
  }

  async deleteClient(clientId: string) {
    const response = await axiosInstance.delete(`${this.basePath}/clients/${clientId}`);
    return response.data;
  }

  /**
   * Dashboard statistics
   */
  async getDashboardStats() {
    const response = await axiosInstance.get(`${this.basePath}/dashboard/stats`);
    return response.data;
  }

  /**
   * Search history
   */
  async getSearchHistory(clientId?: string) {
    const params = clientId ? { clientId } : {};
    const response = await axiosInstance.get(`${this.basePath}/search-history`, { params });
    return response.data;
  }

  /**
   * Subdomain availability check
   */
  async checkSubdomainAvailability(subdomain: string) {
    const response = await axiosInstance.get(`${this.basePath}/check-subdomain/${subdomain}`);
    return response.data;
  }

  /**
   * Get CRM leads for My Customers view
   */
  async getCRMLeads(params?: { page?: number; limit?: number; search?: string }) {
    const response = await axiosInstance.get(`${this.basePath}/crm-leads`, { params });
    return response.data;
  }

  /**
   * Get single client profile with buyer/seller information
   */
  async getClientProfile(leadId: string) {
    const response = await axiosInstance.get(`${this.basePath}/profiles/client/${leadId}`);
    return response.data;
  }

  /**
   * Get search preferences for a lead
   */
  async getSearchPreferences(leadId: string) {
    const response = await axiosInstance.get(`${this.basePath}/search-preferences/${leadId}`);
    return response.data;
  }

  /**
   * Get search profiles
   */
  async getSearchProfiles(params?: { page?: number; limit?: number }) {
    const response = await axiosInstance.get(`${this.basePath}/search-profiles`, { params });
    return response.data;
  }

  /**
   * Get analytics data for the agent dashboard
   */
  async getAnalytics() {
    const response = await axiosInstance.get(`${this.basePath}/analytics`);
    return response.data;
  }

  /**
   * Enable portal access for a lead
   */
  async enablePortalAccess(leadId: string) {
    const response = await axiosInstance.post(`${this.basePath}/enable-portal/${leadId}`);
    return response.data;
  }

  /**
   * Helper to add auth token to requests
   */
  setupInterceptor() {
    axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('agent_token');
        if (token && config.url?.includes('/agents/')) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
}

// Create singleton instance
const agentPortalService = new AgentPortalService();

// Setup auth interceptor
agentPortalService.setupInterceptor();

export default agentPortalService;